/**
 * Employee Readiness Badges for Responsibilities — Rule 13.
 *
 * "When the responsibility has at least one active checkpoint, only
 * issue-in-good-standing is offered." In other words, the badge level
 * dropdown must never offer "Give X" while active checkpoints exist;
 * only "Award X" (for levels whose checkpoints are all passed) is allowed.
 *
 * These helpers are the single "can give" decision shared by
 * GrandBadgeDropdown, InspectionIssueBadge and EmployeeDetail so the rule
 * lives in exactly one place.
 */

export const PROFICIENCY_LEVELS = [
  "apprentice",
  "professional",
  "coach",
  "master",
];

/** Active checkpoint count from a responsibility serializer (v2/v3 `active_checkpoints_count`). */
export function activeCheckpointsFromResponsibility(responsibility) {
  if (!responsibility) return 0;
  if (typeof responsibility.active_checkpoints_count === "number") {
    return responsibility.active_checkpoints_count;
  }
  return 0;
}

/** Active checkpoint count from a per-level `${level}_checkpoint_count` map. */
export function activeCheckpointsFromCounts(counts) {
  if (!counts) return 0;
  return PROFICIENCY_LEVELS.reduce(
    (total, level) => total + (counts[`${level}_checkpoint_count`] || 0),
    0
  );
}

/** Active checkpoint count from a responsibility's per-level `active_${level}_checkpoints` fields. */
export function activeCheckpointsFromPerLevelFields(responsibility) {
  if (!responsibility) return 0;
  return PROFICIENCY_LEVELS.reduce(
    (total, level) => total + (responsibility[`active_${level}_checkpoints`] || 0),
    0
  );
}

/** Active checkpoint count from a list of checkpoint records (`status === "active"`). */
export function activeCheckpointsFromList(checkpoints) {
  if (!Array.isArray(checkpoints)) return 0;
  return checkpoints.filter(
    (checkpoint) => String(checkpoint?.status || "").toLowerCase() === "active"
  ).length;
}

/**
 * Resolve whether a responsibility has active checkpoints from whatever
 * source the host has available. First source with data wins.
 *
 * @param {object} sources
 * @param {object} [sources.responsibility] responsibility serializer
 * @param {object} [sources.checkpointCounts] per-level `${level}_checkpoint_count` map
 * @param {Array}  [sources.checkpoints] checkpoint records with a `status`
 * @returns {boolean}
 */
export function resolveHasActiveCheckpoints({
  responsibility,
  checkpointCounts,
  checkpoints,
} = {}) {
  const count = activeCheckpointsFromResponsibility(responsibility);
  if (count > 0) return true;

  const perLevel = activeCheckpointsFromPerLevelFields(responsibility);
  if (perLevel > 0) return true;

  if (checkpointCounts && activeCheckpointsFromCounts(checkpointCounts) > 0) {
    return true;
  }

  return activeCheckpointsFromList(checkpoints) > 0;
}

/** Rule 13 gate: "Give" is only allowed when no checkpoint is active. */
export function canGiveBadge(hasActiveCheckpoints) {
  return !hasActiveCheckpoints;
}
